import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, Building2 } from 'lucide-react';
import { proveedoresService } from '@/services/proveedores.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';

export function SupplierFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { handleAuthRedirect } = useApiError();
  const isEditing = !!id && id !== 'new';
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '',
    contactName: '',
    phone: '',
    email: '',
    rfc: '',
    address: '',
    paymentTerms: '30',
  });

  useEffect(() => {
    if (isEditing) loadSupplier();
  }, [id]);

  const loadSupplier = async () => {
    setLoading(true);
    try {
      const supplier = await proveedoresService.findById(id!);
      setForm({
        name: supplier.name || '',
        contactName: supplier.contactName || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        rfc: supplier.rfc || '',
        address: supplier.address || '',
        paymentTerms: supplier.paymentTerms || '30',
      });
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError('Error al cargar proveedor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.company?.id) return;
    setSaving(true);
    setError('');

    try {
      const payload = { companyId: user.company.id, ...form };
      if (isEditing) {
        await proveedoresService.update(id!, payload);
      } else {
        await proveedoresService.create(payload);
      }
      navigate('/suppliers');
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al guardar proveedor');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-ios-blue/30 border-t-ios-blue rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/suppliers')} className="p-2 rounded-lg hover:bg-ios-gray-6 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold">{isEditing ? 'Editar Proveedor' : 'Nuevo Proveedor'}</h1>
          <p className="text-sm text-ios-gray mt-1">
            {isEditing ? 'Modifica los datos del proveedor' : 'Agrega un nuevo proveedor'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="ios-card p-6 space-y-4">
          <h2 className="font-semibold text-base flex items-center gap-2">
            <Building2 size={18} className="text-ios-green" />
            Información del Proveedor
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium mb-1.5 block">Nombre *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                className="ios-input w-full"
                placeholder="Ej: Distribuidora de Abarrotes S.A."
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Contacto</label>
              <input
                type="text"
                value={form.contactName}
                onChange={(e) => setForm(p => ({ ...p, contactName: e.target.value }))}
                className="ios-input w-full"
                placeholder="Nombre del contacto"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">RFC</label>
              <input
                type="text"
                value={form.rfc}
                onChange={(e) => setForm(p => ({ ...p, rfc: e.target.value }))}
                className="ios-input w-full"
                placeholder="RFC del proveedor"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Teléfono</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                className="ios-input w-full"
                placeholder="+521234567890"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                className="ios-input w-full"
                placeholder="correo@proveedor.com"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium mb-1.5 block">Dirección</label>
              <textarea
                value={form.address}
                onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
                className="ios-input w-full min-h-[60px] resize-none"
                placeholder="Dirección completa"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Condiciones de pago</label>
              <select
                value={form.paymentTerms}
                onChange={(e) => setForm(p => ({ ...p, paymentTerms: e.target.value }))}
                className="ios-input w-full"
              >
                <option value="CONTADO">Contado</option>
                <option value="15">15 días</option>
                <option value="30">30 días</option>
                <option value="45">45 días</option>
                <option value="60">60 días</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="ios-button flex items-center gap-2">
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Guardando...</>
            ) : (
              <><Save size={18} /> Guardar</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
